import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { VoiceActivityDraft } from '@/api/apis/VoiceActivityApi';

type ActivityEditDialogProps = {
  open: boolean;
  activity: VoiceActivityDraft | null;
  onSave: (updated: VoiceActivityDraft) => void;
  onClose: () => void;
  /** Barnläge: rundare hörn, större touch-targets och text. Default false. */
  child?: boolean;
};

const ActivityEditDialog = ({
  open,
  activity,
  onSave,
  onClose,
  child = false,
}: ActivityEditDialogProps) => {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState<number>(15);

  useEffect(() => {
    if (activity) {
      setName(activity.activityName);
      setStartTime(activity.startTime);
      setDuration(activity.duration);
    }
  }, [activity]);

  const handleSave = () => {
    if (!activity) return;
    onSave({
      ...activity,
      activityName: name.trim(),
      startTime,
      duration: Math.max(1, duration),
    });
  };

  const isValid = name.trim().length > 0 && /^\d{2}:\d{2}$/.test(startTime) && duration > 0;

  // Barnanpassad TextField: rundare hörn, större input/label. Vuxen = undefined
  // (oförändrad MUI-standard).
  const childFieldSx = child
    ? {
        '& .MuiOutlinedInput-root': { borderRadius: '14px', fontSize: 16 },
        '& .MuiInputLabel-root': { fontSize: 15 },
      }
    : undefined;

  const childButtonSx = child
    ? { minHeight: 52, borderRadius: '14px', fontSize: 16, fontWeight: 700 }
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: child ? '16px' : undefined } }}
    >
      <DialogTitle sx={child ? { fontSize: 20, fontWeight: 700 } : undefined}>
        Redigera aktivitet
      </DialogTitle>
      <DialogContent>
        <Stack spacing={child ? 2.5 : 2} pt={1}>
          <TextField
            label="Aktivitetsnamn"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            sx={childFieldSx}
          />
          <TextField
            label="Starttid"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            sx={childFieldSx}
          />
          <TextField
            label="Längd (minuter)"
            type="number"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value, 10) || 0)}
            fullWidth
            inputProps={{ min: 1 }}
            sx={childFieldSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={childButtonSx}>
          Avbryt
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid} sx={childButtonSx}>
          Spara
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityEditDialog;
